import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue'
import { message } from 'ant-design-vue'
import {
  getAliyunLiveRelayStatus,
  startAliyunLiveRelay,
  stopAliyunLiveRelay,
} from '/@/api/manage'
import {
  closeAliyunLivePlayerForStream,
  openAliyunLivePlayer,
} from './aliyun-live-player-state'
import { registerAliyunLiveExitGuard } from './aliyun-live-exit-guard'

type RelayState = 'inactive' | 'connecting' | 'running' | 'stopping' | 'failed'

interface AliyunLiveRelayStatus {
  stream_name: string,
  recording_session_id?: string,
  video_id: string,
  state: RelayState,
  source_matches: boolean,
  owned_by_current_session: boolean,
  play_flv_url?: string,
  error_message?: string,
  updated_at: number
}

interface RelayOptions {
  getVideoId: () => string,
  getRemoteVideoUid: () => string | number | null
}

const CLIENT_SESSION_KEY = 'DJI_CREATE_VITE_H5_APP:aliyun_live_client_session:v1'
const CONNECTING_POLL_MS = 2000
const STABLE_POLL_MS = 10000

export function useAliyunLiveRelay (options: RelayOptions) {
  const loading = ref(false)
  const state = ref<RelayState>('inactive')
  const sourceMatches = ref(true)
  const ownedByCurrentSession = ref(false)
  const streamName = ref('')
  const playFlvUrl = ref('')
  const clientSessionId = getClientSessionId()
  let pollTimer: number | undefined
  let pollVersion = 0
  let restoreVideoId = ''
  let pendingActivation: Promise<void> | null = null
  let lastFailure = ''

  const active = computed(() => sourceMatches.value &&
    (state.value === 'connecting' || state.value === 'running' || state.value === 'stopping'))
  const canPlay = computed(() => sourceMatches.value && state.value === 'running' && !!playFlvUrl.value)
  const otherSourceActive = computed(() => !sourceMatches.value &&
    (state.value === 'connecting' || state.value === 'running' || state.value === 'stopping'))
  const statusText = computed(() => otherSourceActive.value ? '该设备的其他画面正在推送阿里云直播' : '')

  const unregisterExitGuard = registerAliyunLiveExitGuard({
    isBlocking: () => pendingActivation !== null || (active.value && ownedByCurrentSession.value),
    prepareForExit: prepareForExit,
  })
  if (getCurrentInstance()) {
    onBeforeUnmount(unregisterExitGuard)
  }

  async function toggle () {
    if (loading.value) {
      return
    }
    if (active.value) {
      await stop('manual', false)
      return
    }
    const operation = start()
    pendingActivation = operation
    try {
      await operation
    } finally {
      if (pendingActivation === operation) {
        pendingActivation = null
      }
    }
  }

  async function start () {
    const videoId = requireReadyVideoId()
    loading.value = true
    clearPoll()
    try {
      const res = await startAliyunLiveRelay({
        video_id: videoId,
        client_session_id: clientSessionId,
      })
      const status = requireStatus(res, '启动阿里云直播推流失败')
      applyStatus(status, videoId)
      message.success(status.state === 'running' ? '已开始推送阿里云直播' : '阿里云直播推流正在启动')
    } finally {
      loading.value = false
      schedulePoll()
    }
  }

  async function restoreIfNeeded () {
    const videoId = options.getVideoId()
    const remoteUid = options.getRemoteVideoUid()
    if (!videoId || remoteUid === null || remoteUid === undefined || restoreVideoId === videoId) {
      return
    }
    restoreVideoId = videoId
    try {
      await refreshStatus(videoId, true)
    } catch (error: any) {
      message.error(error?.message || '获取阿里云推流状态失败')
    } finally {
      restoreVideoId = ''
      schedulePoll()
    }
  }

  async function stopForSourceShutdown () {
    clearPoll()
    if (pendingActivation) {
      await pendingActivation.catch(() => undefined)
    }
    const videoId = options.getVideoId()
    if (!videoId) {
      return true
    }
    if (!active.value) {
      await refreshStatus(videoId, true).catch(() => undefined)
    }
    if (!active.value || !ownedByCurrentSession.value) {
      return true
    }
    return stop('source_close', true)
  }

  async function prepareForExit () {
    if (pendingActivation) {
      await pendingActivation.catch(() => undefined)
    }
    const videoId = options.getVideoId()
    if (!videoId) {
      return
    }
    await refreshStatus(videoId, true)
  }

  async function stop (reason: 'manual' | 'source_close', silent: boolean) {
    const videoId = options.getVideoId()
    if (!videoId) {
      return true
    }
    loading.value = true
    clearPoll()
    let stopped = false
    try {
      const res = await stopAliyunLiveRelay({
        video_id: videoId,
        client_session_id: clientSessionId,
        reason,
      })
      const status = requireStatus(res, '停止阿里云直播失败')
      applyStatus(status, videoId)
      stopped = status.state === 'inactive' || !status.source_matches
      if (!silent && stopped) {
        message.success('已停止推送阿里云直播')
      }
    } catch (error: any) {
      if (!silent) {
        message.error(error?.message || '停止阿里云直播失败')
      }
    } finally {
      loading.value = false
      if (reason === 'manual') {
        schedulePoll()
      }
    }
    return stopped
  }

  async function showPlayer () {
    const videoId = options.getVideoId()
    if (!videoId) {
      return
    }
    try {
      await refreshStatus(videoId, false)
      if (!canPlay.value) {
        throw new Error('阿里云直播尚未进入可播放状态。')
      }
      openAliyunLivePlayer(streamName.value, playFlvUrl.value)
    } catch (error: any) {
      message.error(error?.message || '获取阿里云播放地址失败')
    }
  }

  async function refreshStatus (videoId = options.getVideoId(), silent = true) {
    if (!videoId) {
      return
    }
    const version = ++pollVersion
    const res = await getAliyunLiveRelayStatus(videoId, clientSessionId)
    if (version !== pollVersion || videoId !== options.getVideoId()) {
      return
    }
    const status = requireStatus(res, '获取阿里云推流状态失败')
    const previousState = state.value
    applyStatus(status, videoId)
    if (!silent && previousState !== 'running' && status.state === 'running') {
      message.success('已开始推送阿里云直播')
    }
  }

  function applyStatus (status: AliyunLiveRelayStatus, requestedVideoId: string) {
    const previousStream = streamName.value
    state.value = status.state || 'inactive'
    sourceMatches.value = status.source_matches !== false
    ownedByCurrentSession.value = status.owned_by_current_session === true
    streamName.value = status.stream_name || getStreamName(requestedVideoId)
    playFlvUrl.value = status.play_flv_url || ''
    if (state.value === 'inactive' || state.value === 'failed' || !sourceMatches.value) {
      closeAliyunLivePlayerForStream(previousStream || streamName.value)
    }
    if (state.value === 'failed') {
      const failure = `${status.updated_at || 0}:${status.error_message || ''}`
      if (failure !== lastFailure) {
        lastFailure = failure
        message.error(status.error_message || '阿里云旁路推流发生错误')
      }
    } else {
      lastFailure = ''
    }
  }

  function schedulePoll () {
    clearPoll()
    const remoteUid = options.getRemoteVideoUid()
    if (!options.getVideoId() || remoteUid === null || remoteUid === undefined) {
      return
    }
    const delay = state.value === 'connecting' || state.value === 'stopping'
      ? CONNECTING_POLL_MS
      : STABLE_POLL_MS
    pollTimer = window.setTimeout(async () => {
      try {
        await refreshStatus(options.getVideoId(), true)
      } catch (_error) {
        // A later poll can recover from a temporary API/network failure.
      } finally {
        schedulePoll()
      }
    }, delay)
  }

  function clearPoll () {
    pollVersion += 1
    if (pollTimer !== undefined) {
      window.clearTimeout(pollTimer)
      pollTimer = undefined
    }
  }

  function requireReadyVideoId () {
    const videoId = options.getVideoId()
    if (!videoId) {
      throw new Error('直播源 video_id 尚未就绪。')
    }
    if (options.getRemoteVideoUid() === null || options.getRemoteVideoUid() === undefined) {
      throw new Error('Agora 视频发布者尚未就绪。')
    }
    return videoId
  }

  return {
    active,
    loading,
    canPlay,
    otherSourceActive,
    statusText,
    toggle,
    showPlayer,
    restoreIfNeeded,
    stopForSourceShutdown,
    refreshStatus,
  }
}

function requireStatus (res: any, fallback: string): AliyunLiveRelayStatus {
  if (res?.code !== 0 || !res?.data?.state) {
    throw new Error(res?.message || fallback)
  }
  return res.data as AliyunLiveRelayStatus
}

function getClientSessionId () {
  try {
    const stored = window.sessionStorage.getItem(CLIENT_SESSION_KEY)
    if (stored) {
      return stored
    }
    const value = createUuid()
    window.sessionStorage.setItem(CLIENT_SESSION_KEY, value)
    return value
  } catch (_error) {
    return createUuid()
  }
}

function createUuid () {
  const randomUuid = window.crypto && (window.crypto as any).randomUUID
  if (typeof randomUuid === 'function') {
    return randomUuid.call(window.crypto)
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16)
    const value = character === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

function getStreamName (videoId: string) {
  return videoId.split('/')[0] || ''
}
